import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Handshake,
  Building2,
  HeartHandshake,
  Home as HomeIcon,
  Users,
  GraduationCap,
  CheckCircle,
  ArrowRight,
  Calendar,
  Briefcase,
  Sparkles,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { colors, images, externalLinks, contact } from "../config/site";

const PARTNER_TYPES = [
  {
    icon: HeartHandshake,
    title: "Nonprofit Organizations",
    body:
      "Strengthen your housing-related programming with workshops and tenant success training delivered to your clients.",
  },
  {
    icon: HomeIcon,
    title: "Shelters & Transitional Housing",
    body:
      "Equip residents preparing to move into independent housing with the practical tools they need to stay housed.",
  },
  {
    icon: Building2,
    title: "Housing Agencies",
    body:
      "Add educational depth to housing placement and stability programs with structured curriculum and coaching.",
  },
  {
    icon: Users,
    title: "Community Organizations",
    body:
      "Bring housing stability education to the families you already serve — financial awareness, lease literacy, and more.",
  },
];

const OFFERINGS = [
  "Customized educational workshops",
  "Tailored training sessions for staff and program participants",
  "Tenant success education series",
  "Housing stability support services",
  "Pre-homeownership readiness workshops",
  "Financial awareness & budgeting sessions",
];

const PROCESS = [
  {
    step: "1",
    title: "Reach Out",
    body: "Share your organization, the audience you serve, and the topics you'd like to cover.",
  },
  {
    step: "2",
    title: "Custom Planning",
    body: "We design workshops or a training series tailored to your participants and goals.",
  },
  {
    step: "3",
    title: "Delivery",
    body: "We deliver the program in-person or virtually, with materials and practical exercises.",
  },
  {
    step: "4",
    title: "Ongoing Support",
    body: "Continue partnering for refresher sessions, expanded curriculum, or new cohorts.",
  },
];

const PartnershipsPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="px-4 pb-14">
          <div className="container mx-auto max-w-6xl">
            <p
              className="text-xs uppercase tracking-widest font-semibold mb-3"
              style={{ color: colors.terracotta }}
            >
              Community Partnerships
            </p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6"
              style={{ color: colors.charcoal }}
            >
              Bring housing stability{" "}
              <span style={{ color: colors.primary }}>resources</span> to the people{" "}
              <span style={{ color: colors.terracotta }}>you serve</span>.
            </h1>
            <p className="text-lg md:text-xl max-w-3xl" style={{ color: colors.textMuted }}>
              Dorsey Taylor Homes delivers workshops and tenant success programming
              through nonprofits, shelters, transitional housing programs, housing agencies,
              and community organizations — directly to the people they support.
            </p>
          </div>
        </section>

        {/* Partner Types */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-2xl mb-12">
              <p
                className="text-xs uppercase tracking-widest font-semibold mb-3"
                style={{ color: colors.terracotta }}
              >
                Who we partner with
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: colors.charcoal }}>
                Designed for organizations on the front lines of housing.
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {PARTNER_TYPES.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-2xl p-7 shadow-sm hover:shadow-md transition-all"
                  style={{ backgroundColor: colors.white, borderLeft: `4px solid ${colors.sage}` }}
                  data-testid={`partner-${title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: colors.charcoal }}>
                    {title}
                  </h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="px-4 pb-20" style={{ backgroundColor: colors.ivory }}>
          <div className="container mx-auto max-w-6xl py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Briefcase className="w-8 h-8 mb-4" style={{ color: colors.terracotta }} />
                <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: colors.charcoal }}>
                  What we deliver through partnerships
                </h2>
                <p className="mb-6" style={{ color: colors.textMuted }}>
                  Every partnership is shaped around your participants. From single workshops to
                  multi-session training cohorts, we build programming that meets your community where
                  it is and supports the outcomes you care about.
                </p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {OFFERINGS.map((o) => (
                    <li key={o} className="flex items-start gap-2 text-sm" style={{ color: colors.charcoal }}>
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.sage }} />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-md">
                <img
                  src={images.workshopAudience}
                  alt="Partner organizations and community members at a workshop"
                  className="w-full h-[440px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-2xl mb-10">
              <p
                className="text-xs uppercase tracking-widest font-semibold mb-3"
                style={{ color: colors.terracotta }}
              >
                How it works
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: colors.charcoal }}>
                A simple process to get programming started.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {PROCESS.map(({ step, title, body }) => (
                <div
                  key={step}
                  className="rounded-2xl p-6 shadow-sm hover:shadow-md transition-all"
                  style={{ backgroundColor: colors.white }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-white font-bold text-base"
                    style={{ backgroundColor: colors.primary }}
                  >
                    {step}
                  </div>
                  <h3 className="font-bold mb-2" style={{ color: colors.charcoal }}>
                    {title}
                  </h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Future vision strip */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div
              className="rounded-3xl p-8 lg:p-10 shadow-md flex flex-col md:flex-row md:items-center gap-6"
              style={{ backgroundColor: colors.white, borderTop: `4px solid ${colors.gold}` }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
              >
                <Sparkles className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-xl lg:text-2xl font-bold mb-2" style={{ color: colors.charcoal }}>
                  Looking ahead: broader community programming.
                </h3>
                <p className="text-sm lg:text-base" style={{ color: colors.textMuted }}>
                  Our long-term vision is to expand into broader community programming — including
                  public workshops, interactive learning tools, and on-demand resources for individuals
                  beyond our partner-delivered programs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4">
          <div className="container mx-auto max-w-5xl">
            <div
              className="rounded-3xl p-10 text-center shadow-md"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              <Handshake className="w-10 h-10 mx-auto mb-4" style={{ color: colors.gold }} />
              <h2 className="text-2xl lg:text-3xl font-bold mb-3">
                Let&apos;s bring this programming to your community.
              </h2>
              <p className="mb-6 opacity-90 max-w-2xl mx-auto">
                Reach out to discuss what your organization needs. We&apos;ll walk you through how a
                partnership with Dorsey Taylor Homes can work.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact" data-testid="partnerships-contact-cta">
                  <Button
                    className="rounded-full px-7 py-6 font-semibold"
                    style={{ backgroundColor: colors.terracotta, color: colors.white }}
                  >
                    Start a Conversation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a
                  href={externalLinks.calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partnerships-calendly-cta"
                >
                  <Button
                    variant="outline"
                    className="rounded-full px-7 py-6 font-semibold border-2"
                    style={{ borderColor: colors.white, color: colors.white, backgroundColor: "transparent" }}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule a Call
                  </Button>
                </a>
              </div>
              <p className="text-sm mt-6 opacity-80">
                Or contact us directly:{" "}
                <a href={contact.phoneHref} className="font-semibold underline">
                  {contact.phone}
                </a>{" "}
                ·{" "}
                <a href={contact.emailHref} className="font-semibold underline">
                  {contact.email}
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PartnershipsPage;
